<script setup>
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import CheckBox from '@/components/Forms/CheckBox.vue'
import { onMounted, ref } from 'vue'
import { useAuthStore } from '@/stores/authStore.js'
import { userApi } from '@/api/User/UserApi.js'
import { Toaster } from '@/models/utils/Toaster.js'
import LoadingSpinner from '@/components/Spinners/LoadingSpinner.vue'
import { scheduledReportsApi } from '@/api/ScheduledReports/ScheduledReportsApi.js'

const reports = ref([])
const checkboxRefs = ref([])
const isSaving = ref(false)
const isLoading = ref(false)
const tenantSettings = ref(null)
const authStore = useAuthStore()
const isDevEnv = ref(import.meta.env.VITE_ENV === 'dev')
const toaster = new Toaster()

onMounted(() => {
	loadSettings()
})

/**
 * Load scheduled report settings.
 */
function loadSettings()
{
	isLoading.value = true

	userApi.getTenantById(useAuthStore().getAuth.user().tenant_id, (tenantData) => {
		tenantSettings.value = JSON.parse(tenantData.settings_json)
		reports.value = tenantSettings.value.scheduled_reports.value

		isLoading.value = false
	})
}

/**
 * Performs checkbox click.
 * @param p_key
 */
function reportClicked(p_key) {
	checkboxRefs.value.forEach((checkbox) => {
		if (checkbox.getInputId() === 'cbx_' + p_key) {
			checkbox.performClick()
		}
	})
}

/**
 * Save changes to scheduled report settings.
 */
function save() {
	isSaving.value = true
	isLoading.value = true

	scheduledReportsApi.updateTenantSettings(reports.value, onSaveComplete)
}

/**
 * Handle response from scheduled report settings update.
 * @param p_response Response from server
 * @param p_error_eh Indicates an error occured while updating scheduled report settings.
 */
function onSaveComplete(p_response, p_error_eh)
{
	try
	{
		isSaving.value = false

		if (p_error_eh)
		{
			p_response.showErrorBanner()
		}
		else
		{
			const data = p_response.getData();
			if (data && data.success)
			{
				toaster.success("Report settings updated!")
				loadSettings()
			}
			else if (data && !data.success)
			{
				toaster.error(data.error_message)
			}
			else
			{
				toaster.error("Failed to create report.")
			}
		}
	}
	catch (e)
	{
		if (authStore.isDeveloper) console.error(e)
		isLoading.value = false
	}
}

</script>

<template>
	<span>Enable and disable reports</span>

	<LoadingSpinner v-if="isLoading" />

	<div v-else class="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] grid-auto-rows-[1fr] gap-4 mt-4">
		<div
			class="flex h-full min-h-[100px] p-4 rounded border border-t-4 border-primary-darker hover:border-primary-bold-hover cursor-pointer"
			v-for="(report, index) in reports"
			:key="index"
			@click="reportClicked(index)"
			v-show="!report.test || (authStore.isDeveloper && isDevEnv)"
		>
			<div class="flex h-full items-center pointer-events-none">
				<CheckBox
					:input-id="'cbx_' + index"
					ref="checkboxRefs"
					v-model="report.enabled"
				/>
				<div class="bg-[#B2BBC9] h-full w-[1px] mx-4"/>
			</div>
			<span class="font-medium my-auto w-fit">{{ report.name }}</span>
		</div>
	</div>

	<SubmitButton class="mt-6" label="Save Settings" @click="save" size="small" :disabled="isSaving" :loading="isSaving"/>
</template>